import React, { useCallback, useRef, useState } from 'react';
import {
  AddSnackbarActionAlertProps,
  AddSnackbarActionAlertType,
  AddSnackbarAlertType,
  SeverityType,
  SnackbarActionAlertType,
  SnackbarHookType,
} from './types';

export const AUTO_HIDE_DURATION = 6000;
export function useSnackbarAlert(): SnackbarHookType {
  const [snackbarAlert, setSnackbarAlert] = useState<SnackbarActionAlertType | null>(null);

  const unmountedRef = useRef(false);

  const addSnackbarActionAlert: AddSnackbarActionAlertType = useCallback(
    ({
      handleClose = () => {
        /* do nothing */
      },
      message,
      severity = 'info',
      autoHideDuration = AUTO_HIDE_DURATION,
      actionFunction = () => {
        // use `autoHideDuration: null` for no auto hide
        // autoHideDuration is automatically set to `null` if `actionMessage` is defined
        /* do nothing */
      },
      actionMessage,
      anchorOrigin = { vertical: 'top', horizontal: 'center' },
    }: AddSnackbarActionAlertProps) => {
      const onClose = () => {
        handleClose();
        setSnackbarAlert(null);
      };
      setSnackbarAlert({
        onClose,
        message,
        severity,
        autoHideDuration: actionMessage ? null : autoHideDuration,
        actionFunction,
        actionMessage,
        anchorOrigin,
      });

      if (actionMessage || autoHideDuration === null || autoHideDuration === undefined) {
        return;
      }
      setTimeout(() => {
        if (unmountedRef.current) {
          return;
        }
        setSnackbarAlert(null);
      }, autoHideDuration);

      return () => {
        unmountedRef.current = true;
      };
    },
    [],
  );

  // An easy way to get a notification with minimal inputs
  const addSnackbarAlert: AddSnackbarAlertType = useCallback(
    (message: React.ReactNode, severity: SeverityType = 'info') => {
      addSnackbarActionAlert({
        message,
        severity,
        autoHideDuration: AUTO_HIDE_DURATION,
      });
    },
    [addSnackbarActionAlert],
  );

  return {
    snackbarAlert,
    addSnackbarAlert,
    addSnackbarActionAlert,
  };
}
